import React, { useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { caseStudies, globalOffices } from '../data/mock';

const impactImages = [
  'https://images.unsplash.com/photo-1705747894609-6a7d17a8cfb7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxoeWRyb2VsZWN0cmljJTIwZGFtfGVufDB8fHx8MTc2MjAyMzc1MXww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1531021713651-fdd4ac075ac1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxiYW1ib28lMjBmb3Jlc3R8ZW58MHx8fHwxNzYyMDI0MDIyfDA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1653406384710-08688ec6b979?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxyZWN5Y2xpbmclMjBmYWNpbGl0eXxlbnwwfHx8fDE3NjIwMjQyODh8MA&ixlib=rb-4.1.0&q=85'
];

const ProjectsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const impactStats = [
    { number: '20M+', label: 'Credits Traded', desc: 'Verified emission reductions' },
    { number: '57M+', label: 'Credits Issued', desc: "Including India's largest for SJVN Hydro" },
    { number: '8+', label: 'Sustainable Domains', desc: 'Wind, Solar, Hydro & More' }
  ];

  return (
    <div className="projects-page bg-white" data-testid="projects-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#FAFAFA] border-b border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl">
            <div className="accent-line mb-6" />
            <span className="overline">Our Impact</span>
            <h1 className="text-[#09090B] mt-4 mb-6">Global Impact & Projects</h1>
            <p className="text-lg md:text-xl text-[#52525B] leading-relaxed">
              Delivering measurable results across renewable energy, waste management, and nature-based solutions
            </p>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
          {impactStats.map((stat, index) => (
            <div key={index} className="p-8 md:p-10 border-l-2 border-[#047857]" data-testid={`project-stat-${index}`}>
              <div className="font-display text-4xl md:text-5xl font-extrabold text-[#09090B] mb-3">{stat.number}</div>
              <div className="text-lg font-bold text-[#09090B] mb-2">{stat.label}</div>
              <p className="text-sm text-[#52525B]">{stat.desc}</p>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 text-center">
          <p className="text-[#52525B] text-base md:text-lg">
            Projects across: <span className="text-[#047857] font-semibold">Wind, Solar, Hydro, Biomass, Waste Management, Biogas, Reforestation, Regenerative Agriculture</span>
          </p>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B] mb-4">Featured Case Studies</h2>
            <p className="text-lg text-[#52525B]">
              Transformative projects delivering real-world environmental and social impact
            </p>
          </div>

          <div className="space-y-px bg-zinc-200 border border-zinc-200 max-w-6xl mx-auto">
            {caseStudies.map((project, index) => (
              <div key={project.id} className="bg-white" data-testid={`case-study-${project.id}`}>
                <div className="grid md:grid-cols-2">
                  <div className={`p-10 md:p-12 flex flex-col justify-center ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                    <div className="flex items-center gap-3 mb-5 flex-wrap">
                      <span className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#047857]/10 text-[#047857]">
                        {project.type}
                      </span>
                      <span className="text-[#047857] font-bold text-lg">{project.credits}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#09090B] mb-4">{project.title}</h3>
                    <p className="text-[#52525B] mb-6 leading-relaxed">{project.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.sdgs.map((sdg) => (
                        <span key={sdg} className="px-3 py-1 text-xs font-semibold text-[#0284C7] border border-[#0EA5E9]/40 bg-[#F0F9FF]">
                          SDG {sdg}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className={`${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                    <img src={impactImages[index % impactImages.length]} alt={project.title} className="w-full h-72 md:h-[400px] object-cover" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-t border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B] mb-4">Global Presence</h2>
            <p className="text-lg text-[#52525B]">
              Operating across continents with local expertise and global reach
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-200 bg-white max-w-7xl mx-auto">
            {globalOffices.map((office) => (
              <div key={office.id} className="p-8 border-b border-r border-zinc-200 hover:bg-[#FAFAFA] transition-colors" data-testid={`office-${office.id}`}>
                <div className="w-12 h-12 bg-[#047857]/10 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-[#047857]" />
                </div>
                <h3 className="text-lg font-bold text-[#09090B] mb-1">{office.city}</h3>
                <p className="text-[#52525B] text-sm mb-4">{office.country}</p>
                <span className="inline-block px-3 py-1 text-xs font-semibold bg-[#047857]/10 text-[#047857]">
                  {office.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
